import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { cn } from '../lib/utils';
import { applyPlatformFavicon, applySpaceFavicon } from '../lib/favicon';
import { resolveSpaceLogoUrl, resolveSpaceFaviconUrl } from '../lib/spaceBranding';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import DocumentFeedback from '../components/documents/DocumentFeedback';
import PublicSearchModal from '../components/search/PublicSearchModal';
import { VideoBlockPreview, FileBlockPreview } from '../components/documents/MediaBlockPreview';
import { API_URL } from '../config';
import {
  Search,
  ChevronRight,
  ChevronDown,
  FileText,
  Loader2,
  Menu,
  X,
  Moon,
  Sun,
  Command,
  AlertTriangle,
} from 'lucide-react';

export default function EmbedDocsPage() {
  const { slug, docId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);

  const [space, setSpace] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [highlightQuery, setHighlightQuery] = useState('');

  const searchAllowed = space?.show_search === true;

  // Branding follows the active theme; falls back to the light asset when no dark
  // variant was uploaded.
  const themedLogoUrl = useMemo(() => resolveSpaceLogoUrl(space, darkMode), [space, darkMode]);
  const themedFaviconUrl = useMemo(() => resolveSpaceFaviconUrl(space, darkMode), [space, darkMode]);

  // Get API key from URL params if needed
  const apiKey = searchParams.get('api_key');

  // iframe embed: ?navbar=0|false or ?hide_navbar=1|true hides the top bar chrome
  // but keeps a burger so the sidebar remains reachable.
  const hideNavbar = useMemo(() => {
    const navbar = (searchParams.get('navbar') || '').toLowerCase();
    const hide = (searchParams.get('hide_navbar') || '').toLowerCase();
    return navbar === '0' || navbar === 'false' || hide === '1' || hide === 'true';
  }, [searchParams]);

  const buildEmbedSearch = useCallback(
    (overrides = {}) => {
      const next = new URLSearchParams();
      if (apiKey) next.set('api_key', apiKey);
      const navbar = searchParams.get('navbar');
      const hide = searchParams.get('hide_navbar');
      if (navbar != null) next.set('navbar', navbar);
      if (hide != null) next.set('hide_navbar', hide);
      Object.entries(overrides).forEach(([key, value]) => {
        if (value == null || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      const qs = next.toString();
      return qs ? `?${qs}` : '';
    },
    [apiKey, searchParams]
  );

  // Keyboard shortcut for search — only when entitled
  useEffect(() => {
    if (!searchAllowed) return undefined;
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchAllowed]);

  // Apply ?q= only when search is entitled; strip from URL otherwise
  useEffect(() => {
    if (!space) return;
    const q = searchParams.get('q');
    if (!searchAllowed) {
      setHighlightQuery('');
      setSearchModalOpen(false);
      if (q) {
        const next = new URLSearchParams(searchParams);
        next.delete('q');
        const qs = next.toString();
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : '' },
          { replace: true }
        );
      }
      return;
    }
    if (q) {
      setHighlightQuery(q);
    } else {
      setHighlightQuery('');
    }
  }, [space, searchAllowed, searchParams, navigate, location.pathname]);

  // Highlight and scroll to matching content
  useEffect(() => {
    if (!searchAllowed || !highlightQuery || !selectedDoc?.content || !contentRef.current) return;

    const timer = setTimeout(() => {
      highlightAndScrollToMatch(highlightQuery);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAllowed, highlightQuery, selectedDoc?.id]);

  const highlightAndScrollToMatch = (query) => {
    if (!contentRef.current || !query) return;

    const existingMarks = contentRef.current.querySelectorAll('mark.search-highlight');
    existingMarks.forEach(mark => {
      const parent = mark.parentNode;
      parent.replaceChild(document.createTextNode(mark.textContent), mark);
      parent.normalize();
    });

    const walker = document.createTreeWalker(
      contentRef.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      if (node.textContent.toLowerCase().includes(query.toLowerCase())) {
        textNodes.push(node);
      }
    }

    if (textNodes.length === 0) return;

    let firstHighlight = null;
    
    textNodes.forEach((textNode) => {
      const text = textNode.textContent;
      const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      
      if (parts.length <= 1) return;

      const fragment = document.createDocumentFragment();
      parts.forEach(part => {
        if (regex.test(part)) {
          const mark = document.createElement('mark');
          mark.className = 'search-highlight bg-yellow-300 dark:bg-yellow-600 text-inherit rounded px-0.5 py-0.5 transition-all';
          mark.textContent = part;
          if (!firstHighlight) {
            firstHighlight = mark;
            mark.className += ' ring-2 ring-yellow-500 ring-offset-2';
          }
          fragment.appendChild(mark);
          regex.lastIndex = 0;
        } else {
          fragment.appendChild(document.createTextNode(part));
        }
      });

      textNode.parentNode.replaceChild(fragment, textNode);
    });

    if (firstHighlight) {
      setTimeout(() => {
        firstHighlight.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  };

  const clearHighlights = useCallback(() => {
    setHighlightQuery('');
    if (contentRef.current) {
      const marks = contentRef.current.querySelectorAll('mark.search-highlight');
      marks.forEach(mark => {
        const parent = mark.parentNode;
        parent.replaceChild(document.createTextNode(mark.textContent), mark);
        parent.normalize();
      });
    }
  }, []);

  const handleSearchResultSelect = useCallback(
    (result) => {
      if (!searchAllowed) return;
      const baseUrl = `/embed/${slug}/${result.id}`;
      navigate(`${baseUrl}${buildEmbedSearch({ q: result.searchQuery })}`);
    },
    [navigate, slug, buildEmbedSearch, searchAllowed]
  );

  // Favicon lives in its own effect so it re-applies when the theme is toggled,
  // not only when the space first loads.
  useEffect(() => {
    if (themedFaviconUrl) applySpaceFavicon(themedFaviconUrl);
  }, [themedFaviconUrl]);

  // Apply custom styling
  useEffect(() => {
    if (!space) return;

    if (space.custom_css) {
      const styleId = 'embed-custom-css';
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = space.custom_css;
    }

    document.documentElement.style.setProperty('--docs-primary', space.primary_color || '#2563eb');
    document.documentElement.style.setProperty('--docs-secondary', space.secondary_color || '#64748b');

    return () => {
      const styleEl = document.getElementById('embed-custom-css');
      if (styleEl) styleEl.remove();
      applyPlatformFavicon();
    };
  }, [space]);

  // Fetch space data
  useEffect(() => {
    if (!slug) return;
    
    const params = apiKey ? `?api_key=${apiKey}` : '';
    
    axios.get(`${API_URL}/embed/${slug}/space${params}`)
      .then(res => {
        setSpace(res.data);
        if (res.data.theme === 'dark') setDarkMode(true);
      })
      .catch(err => {
        // Keep the server's explanation (403 access denied, 402 billing suspended,
        // ...) instead of flattening everything into "not found".
        setError(err.response?.data?.detail || 'Documentation not found');
        setLoading(false);
      });
  }, [slug, apiKey]);

  // Fetch documents
  useEffect(() => {
    if (!space?.id) return;
    
    const params = apiKey ? `?api_key=${apiKey}` : '';
    
    axios.get(`${API_URL}/embed/${slug}/documents${params}`)
      .then(res => {
        setDocuments(res.data);
        const exp = {};
        res.data.forEach(d => { exp[d.id] = true; });
        setExpanded(exp);
        
        if (docId) {
          axios.get(`${API_URL}/embed/${slug}/documents/${docId}${params}`)
            .then(r => setSelectedDoc(r.data))
            .catch(console.error);
        } else if (res.data.length > 0) {
          const baseUrl = `/embed/${slug}/${res.data[0].id}`;
          navigate(`${baseUrl}${buildEmbedSearch()}`, { replace: true });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  // buildEmbedSearch omitted from deps: only needed for initial redirect; avoid re-fetch loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space?.id, slug, docId, navigate, apiKey]);

  // Fetch document when docId changes
  useEffect(() => {
    if (!space?.id || !docId) return;
    
    if (!searchParams.get('q')) {
      clearHighlights();
    }
    
    const params = apiKey ? `?api_key=${apiKey}` : '';
    
    axios.get(`${API_URL}/embed/${slug}/documents/${docId}${params}`)
      .then(res => setSelectedDoc(res.data))
      .catch(console.error);
  }, [docId, space?.id, slug, apiKey, searchParams, clearHighlights]);

  const handleSelectDoc = (id) => {
    clearHighlights();
    const baseUrl = `/embed/${slug}/${id}`;
    navigate(`${baseUrl}${buildEmbedSearch()}`);
    setSidebarOpen(false);
  };

  const handleToggle = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderTreeItem = (doc, level = 0) => {
    const hasChildren = doc.children && doc.children.length > 0;
    const isExpanded = expanded[doc.id];
    const isSelected = selectedDoc?.id === doc.id;
    const primaryColor = space?.primary_color || '#2563eb';

    return (
      <div key={doc.id}>
        <button
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors text-left',
            isSelected
              ? 'font-medium'
              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
          )}
          style={isSelected ? { 
            backgroundColor: `${primaryColor}15`,
            color: primaryColor
          } : { paddingLeft: `${12 + level * 16}px` }}
          onClick={() => handleSelectDoc(doc.id)}
          data-testid={`embed-doc-item-${doc.id}`}
        >
          {hasChildren && (
            <span
              onClick={(e) => { e.stopPropagation(); handleToggle(doc.id); }}
              className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded cursor-pointer"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          )}
          {!hasChildren && <FileText className="w-4 h-4 text-slate-400" />}
          <span className="truncate">{doc.title}</span>
        </button>
        {hasChildren && isExpanded && doc.children.map(child => renderTreeItem(child, level + 1))}
      </div>
    );
  };

  const renderContent = () => {
    if (!selectedDoc?.content) return null;
    
    let blocks = [];
    try {
      const data = JSON.parse(selectedDoc.content);
      blocks = data.blocks || [];
    } catch {
      return <p className="text-slate-600">Unable to render content</p>;
    }

    return blocks.map((block, index) => {
      const key = `block-${index}`;
      
      if (block.type === 'header') {
        const level = block.data?.level || 2;
        const cls = level === 1 ? 'text-4xl font-bold mt-8 mb-4' : 
                    level === 2 ? 'text-3xl font-bold mt-8 mb-4' :
                    level === 3 ? 'text-2xl font-semibold mt-6 mb-3' : 'text-xl font-semibold mt-4 mb-2';
        return <div key={key} id={`heading-${index}`} className={cn(cls, 'text-slate-900 dark:text-white')} dangerouslySetInnerHTML={{ __html: block.data?.text || '' }} />;
      }
      
      if (block.type === 'paragraph') {
        return <p key={key} className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: block.data?.text || '' }} />;
      }
      
      if (block.type === 'list') {
        const items = block.data?.items || [];
        const Tag = block.data?.style === 'ordered' ? 'ol' : 'ul';
        const listCls = block.data?.style === 'ordered' ? 'list-decimal list-inside' : 'list-disc list-inside';
        return (
          <Tag key={key} className={cn(listCls, 'mb-4 space-y-1 text-slate-700 dark:text-slate-300')}>
            {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: typeof item === 'string' ? item : item.content || '' }} />)}
          </Tag>
        );
      }
      
      if (block.type === 'code') {
        return (
          <pre key={key} className="bg-slate-900 dark:bg-slate-950 rounded-lg p-4 mb-4 overflow-x-auto">
            <code className="text-sm text-slate-100 font-mono">{block.data?.code || ''}</code>
          </pre>
        );
      }
      
      if (block.type === 'quote') {
        return (
          <blockquote key={key} className="border-l-4 border-blue-500 pl-4 italic text-slate-600 dark:text-slate-400 mb-4">
            <p dangerouslySetInnerHTML={{ __html: block.data?.text || '' }} />
          </blockquote>
        );
      }
      
      if (block.type === 'callout') {
        const colors = {
          info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 text-blue-800 dark:text-blue-200',
          warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 text-amber-800 dark:text-amber-200',
          success: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 text-emerald-800 dark:text-emerald-200',
          danger: 'bg-red-50 dark:bg-red-900/20 border-red-200 text-red-800 dark:text-red-200',
        };
        return (
          <div key={key} className={cn('rounded-lg border p-4 mb-4', colors[block.data?.type] || colors.info)}>
            <p dangerouslySetInnerHTML={{ __html: block.data?.message || '' }} />
          </div>
        );
      }
      
      if (block.type === 'delimiter') {
        return <hr key={key} className="my-8 border-slate-200 dark:border-slate-700" />;
      }
      
      // Image block
      if (block.type === 'image') {
        const url = block.data?.url;
        const caption = block.data?.caption;
        const stretched = block.data?.stretched;
        if (!url) return null;
        return (
          <figure key={key} className={cn('mb-6', stretched ? 'w-full' : 'max-w-2xl mx-auto')}>
            <img src={url} alt={block.data?.alt || caption || ''} className="rounded-lg w-full" />
            {caption && <figcaption className="mt-2 text-sm text-center text-slate-500">{caption}</figcaption>}
          </figure>
        );
      }
      
      // Video block
      if (block.type === 'video') {
        const url = block.data?.url;
        if (!url) return null;
        return <VideoBlockPreview key={key} url={url} caption={block.data?.caption} />;
      }
      
      // Embed block
      if (block.type === 'embed') {
        const url = block.data?.url;
        if (!url) return null;
        return (
          <div key={key} className="mb-6 aspect-video">
            <iframe src={url} className="w-full h-full rounded-lg border border-slate-200 dark:border-slate-700" allowFullScreen title="Embedded content" />
          </div>
        );
      }
      
      // File block
      if (block.type === 'file') {
        const url = block.data?.url;
        const filename = block.data?.filename || block.data?.name || url?.split('/').pop() || 'Download file';
        if (!url) return null;
        return (
          <FileBlockPreview
            key={key}
            url={url}
            filename={filename}
            size={block.data?.size}
            variant="embed"
          />
        );
      }
      
      // Table block
      if (block.type === 'table') {
        const content = block.data?.content || [];
        if (content.length === 0) return null;
        return (
          <div key={key} className="mb-6 overflow-x-auto">
            <table className="w-full border-collapse border border-slate-200 dark:border-slate-700">
              <tbody>
                {content.map((row, rowIndex) => (
                  <tr key={rowIndex} className={rowIndex === 0 && block.data?.withHeadings ? 'bg-slate-50 dark:bg-slate-800' : ''}>
                    {row.map((cell, cellIndex) => {
                      const Tag = rowIndex === 0 && block.data?.withHeadings ? 'th' : 'td';
                      return <Tag key={cellIndex} className="border border-slate-200 dark:border-slate-700 px-4 py-2 text-left" dangerouslySetInnerHTML={{ __html: cell }} />;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      
      return null;
    });
  };

  const getHeadings = () => {
    if (!selectedDoc?.content) return [];
    try {
      const data = JSON.parse(selectedDoc.content);
      return (data.blocks || [])
        .map((block, index) => block.type === 'header' ? {
          id: `heading-${index}`,
          text: (block.data?.text || '').replace(/<[^>]+>/g, ''),
          level: block.data?.level || 2
        } : null)
        .filter(Boolean);
    } catch {
      return [];
    }
  };

  if (loading) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center', darkMode ? 'dark bg-slate-950' : 'bg-white')}>
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('min-h-screen flex items-center justify-center p-8', darkMode ? 'dark bg-slate-950' : 'bg-white')}>
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Access Denied</h1>
          <p className="text-slate-600 dark:text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  const headings = getHeadings();
  const headerHeightClass = hideNavbar ? 'top-10' : 'top-12';
  const sidebarHeightClass = hideNavbar ? 'h-[calc(100vh-2.5rem)]' : 'h-[calc(100vh-3rem)]';

  return (
    <div className={cn('min-h-screen', darkMode ? 'dark' : '')} data-testid="embed-docs-page">
      <div className="bg-white dark:bg-slate-950 min-h-screen">
        {/* Compact Header for Embed — slim chrome when ?navbar=0 */}
        <header
          className={cn(
            'sticky top-0 z-40 bg-white/90 dark:bg-slate-950/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800',
            hideNavbar && 'border-b-0'
          )}
          data-testid="embed-header"
        >
          <div className={cn('px-4 flex items-center justify-between', hideNavbar ? 'h-10' : 'h-12')}>
            <div className="flex items-center gap-3">
              <button
                className={cn(
                  'p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg',
                  !hideNavbar && 'lg:hidden'
                )}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                data-testid="embed-menu-toggle"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              {!hideNavbar && (
                <div className="flex items-center gap-2">
                  {themedLogoUrl ? (
                    <img src={themedLogoUrl} alt={space.name} className="h-6" />
                  ) : (
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: space?.primary_color || '#2563eb' }}>
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <span className="font-medium text-sm text-slate-900 dark:text-white">{space?.name}</span>
                </div>
              )}
            </div>

            {!hideNavbar && (
              <div className="flex items-center gap-2">
                {searchAllowed && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0 rounded-full text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white sm:h-8 sm:w-auto sm:rounded-md sm:gap-1.5 sm:px-2.5"
                    onClick={() => searchAllowed && setSearchModalOpen(true)}
                    data-testid="embed-search-btn"
                    aria-label="Search docs"
                  >
                    <Search className="h-4 w-4 shrink-0" />
                    <kbd className="hidden items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800 sm:inline-flex">
                      <Command className="h-3 w-3" />K
                    </kbd>
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => { setDarkMode(!darkMode); document.documentElement.classList.toggle('dark', !darkMode); }}>
                  {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
        </header>

        <div className="flex">
          {/* Sidebar */}
          <aside
            className={cn(
              'fixed left-0 z-30 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform dark:border-slate-800 dark:bg-slate-950',
              headerHeightClass,
              sidebarHeightClass,
              hideNavbar ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : cn('lg:sticky lg:translate-x-0', sidebarOpen ? 'translate-x-0' : '-translate-x-full')
            )}
            data-testid="embed-sidebar"
          >
            <div className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="min-h-0 flex-1 py-3">
                <nav className="space-y-0.5 px-2">
                  {documents.map((doc) => renderTreeItem(doc, 0))}
                </nav>
              </ScrollArea>
              <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-950">
                <a
                  href="https://doc-tree.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center text-[11px] leading-snug text-slate-500 transition-colors hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  data-testid="embed-powered-by"
                >
                  Powered by{' '}
                  <span className="font-semibold text-slate-700 dark:text-slate-300">DocTree</span>
                </a>
              </div>
            </div>
          </aside>

          {sidebarOpen && (
            <div
              className={cn('fixed inset-0 bg-black/50 z-20', !hideNavbar && 'lg:hidden')}
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-testid="embed-main">
            <div className="max-w-3xl mx-auto px-4 py-6 lg:px-8">
              {selectedDoc ? (
                <div ref={contentRef} data-testid="embed-content">
                  <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-6">{selectedDoc.title}</h1>
                  
                  {searchAllowed && highlightQuery && (
                    <div className="mb-4 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center justify-between text-sm">
                      <span className="text-yellow-800 dark:text-yellow-200">
                        Showing results for "<strong>{highlightQuery}</strong>"
                      </span>
                      <Button variant="ghost" size="sm" onClick={clearHighlights} className="text-yellow-700 hover:text-yellow-900 h-7">
                        <X className="w-3 h-3 mr-1" />Clear
                      </Button>
                    </div>
                  )}
                  
                  <div className="doc-tree-rich prose dark:prose-invert max-w-none">{renderContent()}</div>
                  
                  {selectedDoc.updated_at && (
                    <p className="mt-8 text-xs text-slate-500">
                      Last updated: {new Date(selectedDoc.updated_at).toLocaleDateString()}
                    </p>
                  )}
                  
                  {selectedDoc.show_feedback_panel !== false && (
                    <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
                      <DocumentFeedback documentId={selectedDoc.id} isPublic={true} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <h2 className="text-lg font-medium text-slate-900 dark:text-white mb-1">Select a document</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Choose from the sidebar.</p>
                </div>
              )}
            </div>
          </main>

          {/* Table of Contents */}
          {space?.show_toc && headings.length > 0 && (
            <div className="hidden xl:block w-56 flex-shrink-0">
              <div className={cn('sticky p-3', hideNavbar ? 'top-12' : 'top-16')}>
                <h4 className="text-xs font-semibold text-slate-900 dark:text-white mb-2 uppercase tracking-wider">On this page</h4>
                <nav className="space-y-0.5">
                  {headings.map(h => (
                    <a key={h.id} href={`#${h.id}`} className={cn('block text-xs py-1 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white', h.level === 2 ? 'pl-0' : h.level === 3 ? 'pl-2' : 'pl-4')}>
                      {h.text}
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          )}
        </div>

        {space?.footer_text && (
          <footer className="border-t border-slate-200 dark:border-slate-800 py-4 text-center text-xs text-slate-500">
            {space.footer_text}
          </footer>
        )}
      </div>

      {searchAllowed ? (
        <PublicSearchModal
          isOpen={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
          spaceId={space?.id}
          spaceName={space?.name}
          onSelectResult={handleSearchResultSelect}
        />
      ) : null}
    </div>
  );
}
